/**
 * Universal Auth Context
 * Gestion centralisée de l'authentification pour TOUS les utilisateurs
 * - Access token en mémoire (state)
 * - Refresh token dans localStorage
 * - Auto-refresh sur 401 via intercepteur
 * - Bootstrap de session au montage
 */

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import api, { setAccessToken, clearAccessToken, attachAuthInterceptors } from "../services/api";
import {
  getStoredRefreshToken,
  setStoredRefreshToken,
  clearStoredTokens,
} from "../utils/tokenStorage";

const UniversalAuthContext = createContext(null);

export const UniversalAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const interceptorsAttached = useRef(false);

  /**
   * Récupérer les infos de l'utilisateur connecté
   */
  const fetchMe = useCallback(async () => {
    const response = await api.get("/api/auth/me");
    const me = response.data;
    setUser(me);
    return me;
  }, []);

  /**
   * Déconnexion
   */
  const logout = useCallback(async ({ callApi = true } = {}) => {
    try {
      if (callApi) {
        await api.post("/api/auth/logout");
      }
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      clearAccessToken();
      clearStoredTokens();
      setUser(null);
    }
  }, []);

  /**
   * Rafraîchir les tokens
   */
  const refreshAuth = useCallback(async () => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await api.post("/api/auth/refresh", {
      refresh_token: refreshToken,
    });

    const newAccessToken = response.data?.access_token;
    const newRefreshToken = response.data?.refresh_token;
    const refreshedUser = response.data?.user;

    if (!newAccessToken) {
      throw new Error("No access token returned from refresh");
    }

    setAccessToken(newAccessToken);

    if (newRefreshToken) {
      setStoredRefreshToken(newRefreshToken);
    }

    if (refreshedUser) {
      setUser(refreshedUser);
      return refreshedUser;
    }

    // Si pas de user dans la réponse, le fetch
    return await fetchMe();
  }, [fetchMe]);

  /**
   * Login universel
   */
  const login = useCallback(async ({ email, password }) => {
    const response = await api.post("/api/auth/login", {
      email: email.trim().toLowerCase(),
      password,
    });

    const accessToken = response.data?.access_token;
    const refreshToken = response.data?.refresh_token;
    const loggedUser = response.data?.user;

    if (!accessToken || !refreshToken || !loggedUser) {
      throw new Error("Réponse d'authentification invalide");
    }

    setAccessToken(accessToken);
    setStoredRefreshToken(refreshToken);
    setUser(loggedUser);

    return loggedUser;
  }, []);

  /**
   * Changer le mot de passe
   */
  const changePassword = useCallback(async ({ current_password, new_password }) => {
    return await api.put("/api/auth/change-password", {
      current_password,
      new_password,
    });
  }, []);

  /**
   * Vérifier si l'utilisateur a un rôle spécifique
   */
  const hasRole = useCallback(
    (roles = []) => {
      if (!user) return false;
      return roles.includes(user.role);
    },
    [user]
  );

  /**
   * Vérifier si l'utilisateur a une permission
   */
  const hasPermission = useCallback(
    (permission) => {
      if (!user) return false;
      if (user.role === "super_admin") return true;

      const permissions = user.permissions || [];
      if (permissions.includes("*")) return true;
      if (permissions.includes(permission)) return true;

      // Wildcard resource (ex: "clients.*")
      const [resource] = permission.split(".");
      return permissions.includes(`${resource}.*`);
    },
    [user]
  );

  /**
   * Bootstrap de session au montage
   * Tente un refresh si un refresh token existe
   */
  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      try {
        const refreshToken = getStoredRefreshToken();

        if (!refreshToken) {
          if (isMounted) setAuthLoading(false);
          return;
        }

        // Tenter de restaurer la session
        await refreshAuth();
      } catch (error) {
        console.error("Auth bootstrap failed:", error);
        await logout({ callApi: false });
      } finally {
        if (isMounted) setAuthLoading(false);
      }
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, [refreshAuth, logout]);

  /**
   * Attacher les intercepteurs Axios UNE SEULE FOIS
   */
  useEffect(() => {
    if (interceptorsAttached.current) return;

    attachAuthInterceptors({
      refreshAuth,
      logout,
    });

    interceptorsAttached.current = true;
  }, [refreshAuth, logout]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      authLoading,
      login,
      logout,
      refreshAuth,
      fetchMe,
      changePassword,
      hasRole,
      hasPermission,
      setUser,
    }),
    [user, authLoading, login, logout, refreshAuth, fetchMe, changePassword, hasRole, hasPermission]
  );

  return (
    <UniversalAuthContext.Provider value={value}>
      {children}
    </UniversalAuthContext.Provider>
  );
};

export const useUniversalAuth = () => {
  const context = useContext(UniversalAuthContext);
  if (!context) {
    throw new Error("useUniversalAuth must be used within UniversalAuthProvider");
  }
  return context;
};
